/**
 * Pedir cotizacion a proveedores: el formulario, el correo y la lista.
 *
 * ---------------------------------------------------------------------------
 * ESTA PANTALLA NO REDACTA EL CORREO
 *
 * El asunto y el cuerpo llegan hechos desde la API, en `detalle.correos`, y
 * aqui solo se pintan y se copian. No es pereza: el correo va en el idioma del
 * PROVEEDOR --el de `tercero_contacto.idioma`-- y no en el de la interfaz.
 * Redactarlo aqui lo ataria a los diccionarios de i18n, que responden a la
 * preferencia de quien mira la pantalla, y un agente que trabaja en español
 * acabaria mandando un RFQ en español a quien contesta en ingles.
 *
 * El beneficio de fondo es otro: hay UN solo texto. El que se previsualiza es
 * el mismo que un dia mandara Microsoft Graph, byte por byte, porque sale del
 * mismo sitio.
 *
 * ---------------------------------------------------------------------------
 * COPIAR NO ES ENVIAR, Y SE DICE
 *
 * El boton copia al portapapeles y punto. "Marcar como enviada" es una
 * afirmacion del operador sobre algo que hizo fuera de la aplicacion, y el
 * aviso lo deja escrito en la pantalla en vez de en un comentario: quien
 * mañana vea una solicitud ENVIADA tiene que saber que eso significa que
 * alguien dijo que la mando, no que el sistema la mandara.
 *
 * ---------------------------------------------------------------------------
 * LOS PROVEEDORES QUE NO CUBREN LA RUTA SE VEN
 *
 * Van en un segundo grupo, sin marcar, con el motivo delante. Esconderlos
 * convertiria un dato incompleto --una especialidad que nadie declaro-- en una
 * ausencia invisible, y el operador que sabe que ese proveedor si hace la ruta
 * no tendria forma de añadirlo. Es la misma linea que la desambiguacion de
 * puertos: la maquina propone y el humano decide.
 *
 * Un proveedor sin correo en el directorio aparece con la casilla desactivada y
 * el motivo escrito. Una casilla que no se puede marcar y no dice por que es
 * peor que una fila que falta.
 */

import type {
  DetalleSolicitudRfq,
  EquipoCatalogo,
  ProveedorRfq,
  ResumenSolicitudRfq,
} from '../api';
import { dato, el, literal } from '../dom';
import { pintarCarga } from './carga';
import type { FormularioCarga } from '../carga';
import { formatearFecha, type Idioma } from '../formato';
import type { Traductor } from '../i18n';
import { MAX_UNIDADES, type FormularioRfq } from '../peticiones';

/** El estado del formulario vive en `app.ts`; aqui solo se pinta y se avisa de
 *  los cambios. */
export type EstadoFormularioRfq = FormularioRfq;

export interface OpcionesFormularioRfq {
  readonly t: Traductor;
  readonly valores: EstadoFormularioRfq;
  readonly equipos: readonly EquipoCatalogo[];
  readonly proveedores: readonly ProveedorRfq[];
  readonly cargandoProveedores: boolean;
  readonly creando: boolean;
  readonly faltan: readonly string[];
  /** Solo cuando cambia la NATURALEZA de la carga, que es lo unico que obliga
   *  a repintar: cambian los campos de debajo. Nunca al teclear. */
  readonly onCambio: (v: EstadoFormularioRfq) => void;
  readonly onCrear: (v: EstadoFormularioRfq) => void;
  readonly onCancelar: () => void;
}

function campo(
  etiqueta: string,
  control: HTMLElement,
  ayuda?: string,
  clase = 'campo',
): HTMLElement {
  return el('div', { clase }, [
    el('label', { clase: 'etiqueta', texto: etiqueta, attrs: { for: control.id } }),
    control,
    ayuda === undefined ? null : el('span', { clase: 'ayuda tenue', texto: ayuda }),
  ]);
}

/** Un campo de texto suelto: sin manejador. Se lee cuando hace falta. Ver la
 *  cabecera de `pintarFormularioRfq`. */
function entrada(
  id: string,
  valor: string,
  attrs: Record<string, string> = {},
): HTMLInputElement {
  return el('input', {
    clase: 'control',
    attrs: { id, type: 'text', value: valor, ...attrs },
  });
}

/**
 * El formulario de RFQ.
 *
 * ===========================================================================
 * LOS CAMPOS SE LEEN AL MANDAR, NO AL TECLEAR
 *
 * Antes cada tecla llamaba a `onCambio`, y `app.ts` respondia repintando el
 * panel entero: se escribia una letra en "Mercancia" y el cursor se iba. Ademas
 * el `{ ...v, ...parcial }` de cada manejador parte de la `v` que habia cuando
 * se CONSTRUYO la vista, asi que sin ese repintado los campos se pisarian entre
 * ellos. Mismo patron y mismo motivo que en `vista/login.ts` y
 * `vista/contrasena.ts`.
 *
 * Ahora los controles van sueltos y `leer()` los recoge cuando hace falta: al
 * mandar, y al cambiar la naturaleza de la carga --que si repinta, porque
 * cambian los campos de debajo, y ahi el foco esta en un desplegable que se
 * acaba de usar--.
 */
export function pintarFormularioRfq(o: OpcionesFormularioRfq): HTMLElement {
  const { t, valores: v } = o;

  const ruta =
    `${v.pol}${v.polPais === null ? '' : ` (${v.polPais})`} → ` +
    `${v.pod}${v.podPais === null ? '' : ` (${v.podPais})`}`;

  const equipo = el('select', { clase: 'control', attrs: { id: 'rfq-equipo' } });
  equipo.appendChild(el('option', { texto: '—', attrs: { value: '' } }));
  for (const e of o.equipos) {
    equipo.appendChild(
      el('option', { texto: `${e.codigo} · ${e.nombre}`, attrs: { value: e.codigo } }),
    );
  }
  equipo.value = v.equipo;

  const unidades = el('input', {
    clase: 'control',
    attrs: {
      id: 'rfq-unidades',
      type: 'number',
      min: '1',
      max: String(MAX_UNIDADES),
      step: '1',
      value: v.unidades === null ? '' : String(v.unidades),
    },
  });

  const mercancia = entrada('rfq-mercancia', v.mercancia);
  const requisitos = entrada('rfq-requisitos', v.requisitos);
  const nota = entrada('rfq-nota', v.nota);

  // Los decimales son campos de TEXTO y no `type=number`. Un input numerico
  // devuelve `valueAsNumber` --un flotante-- y ademas cambia de formato segun
  // la configuracion regional del navegador. Aqui la cadena que escribe el
  // agente es la que viaja. [CLAUDE.md, Dinero]
  const peso = entrada('rfq-peso', v.pesoKg, { inputmode: 'decimal' });
  const volumen = entrada('rfq-volumen', v.volumenM3, { inputmode: 'decimal' });

  const fecha = el('input', {
    clase: 'control',
    attrs: { id: 'rfq-fecha', type: 'date', value: v.fechaObjetivo },
  });

  const remitente = el('select', { clase: 'control', attrs: { id: 'rfq-remitente' } });
  for (const r of ['AGS', 'FREIGHT_BRIDGE'] as const) {
    remitente.appendChild(
      el('option', { texto: t(`rfq.remitente${r}`), attrs: { value: r } }),
    );
  }
  remitente.value = v.remitente;

  for (const [nombre, control] of [
    ['unidades', unidades],
    ['peso_kg', peso],
    ['volumen_m3', volumen],
    ['fecha_objetivo', fecha],
  ] as const) {
    if (o.faltan.includes(nombre)) control.classList.add('falta');
  }

  /** Los proveedores marcados AHORA. La casilla ya guarda su estado; esto solo
   *  lo recoge. */
  const marcados: string[] = [...v.proveedores];

  /** Lo que hay escrito ahora mismo, no lo que habia al construir la vista. */
  const leer = (): EstadoFormularioRfq => ({
    ...v,
    equipo: equipo.value,
    unidades: Number.isFinite(unidades.valueAsNumber) ? unidades.valueAsNumber : null,
    mercancia: mercancia.value,
    requisitos: requisitos.value,
    pesoKg: peso.value,
    volumenM3: volumen.value,
    // La temperatura la lleva ahora el bloque de carga: cuando la naturaleza es
    // REFRIGERADA sale de ahi, y `armarSolicitudRfq` la manda por los dos
    // caminos --la columna de la 0017 y la declaracion-- sin contradecirse.
    temperaturaC: bloqueCarga.valores.temperaturaC,
    fechaObjetivo: fecha.value,
    nota: nota.value,
    remitente: remitente.value,
    proveedores: [...marcados],
    carga: bloqueCarga.valores,
  });

  // El bloque de "que es lo que viaja". Su unico cambio que repinta es la
  // naturaleza; los demas campos se leen igual que estos.
  const bloqueCarga = { valores: v.carga };
  const nodoCarga = pintarCarga({
    t,
    valores: v.carga,
    prefijo: 'rfq-carga',
    faltan: o.faltan,
    onCambio: (nueva: FormularioCarga) => {
      const cambioNaturaleza = nueva.naturaleza !== bloqueCarga.valores.naturaleza;
      bloqueCarga.valores = nueva;
      // Solo la naturaleza obliga a repintar: cambian los campos de debajo.
      if (cambioNaturaleza) o.onCambio(leer());
    },
  });

  const contador = el('span', {
    clase: 'tenue',
    texto: t('rfq.proveedores.elegidos', { n: String(marcados.length) }),
  });

  return el('section', { clase: 'panel panel-rfq' }, [
    el('header', { clase: 'rfq-cabecera' }, [
      el('h2', { texto: t('rfq.titulo') }),
      el('span', { clase: 'tenue', texto: `${t('rfq.ruta')}: ${ruta} · ${v.modo}` }),
    ]),

    el('div', { clase: 'rfq-bloque' }, [
      el('h3', { texto: t('rfq.carga.titulo') }),
      el('div', { clase: 'formulario-rfq' }, [
        campo(t('rfq.carga.equipo'), equipo),
        campo(t('rfq.carga.unidades'), unidades),
        campo(t('rfq.carga.fechaObjetivo'), fecha),
        campo(t('rfq.carga.peso'), peso),
        campo(t('rfq.carga.volumen'), volumen),
        campo(
          t('rfq.carga.mercancia'),
          mercancia,
          t('rfq.carga.mercanciaAyuda'),
          'campo campo-ancho',
        ),
        campo(
          t('rfq.carga.requisitos'),
          requisitos,
          t('rfq.carga.requisitosAyuda'),
          'campo campo-ancho',
        ),
        campo(t('rfq.carga.nota'), nota, undefined, 'campo campo-ancho'),
        campo(t('rfq.remitente'), remitente),
      ]),
      // Que es lo que viaja. Va aqui, con el resto de la carga, y no en un
      // bloque aparte: es la misma pregunta.
      nodoCarga,
    ]),

    pintarSeleccionProveedores(o, marcados, () => {
      contador.textContent = t('rfq.proveedores.elegidos', {
        n: String(marcados.length),
      });
    }),

    el('footer', { clase: 'rfq-acciones' }, [
      contador,
      o.faltan.includes('destinatarios')
        ? el('span', { clase: 'aviso-inline', texto: t('rfq.proveedores.eligeUno') })
        : null,
      el('button', {
        clase: 'boton boton-plano',
        texto: t('rfq.cancelar'),
        attrs: { type: 'button' },
        onClick: o.onCancelar,
      }),
      el('button', {
        clase: 'boton boton-primario',
        texto: o.creando ? t('rfq.creando') : t('rfq.crear'),
        attrs: o.creando ? { type: 'button', disabled: 'disabled' } : { type: 'button' },
        onClick: () => {
          o.onCrear(leer());
        },
      }),
    ]),
  ]);
}

function pintarSeleccionProveedores(
  o: OpcionesFormularioRfq,
  marcados: string[],
  onMarcar: () => void,
): HTMLElement {
  const { t, valores: v } = o;

  if (o.cargandoProveedores) {
    return el('div', { clase: 'rfq-bloque' }, [
      el('h3', { texto: t('rfq.proveedores.titulo') }),
      el('p', { clase: 'tenue', texto: t('app.cargando') }),
    ]);
  }

  if (o.proveedores.length === 0) {
    return el('div', { clase: 'rfq-bloque' }, [
      el('h3', { texto: t('rfq.proveedores.titulo') }),
      el('p', { clase: 'tenue', texto: t('rfq.proveedores.ninguno') }),
    ]);
  }

  const cubren = o.proveedores.filter((p) => p.precision !== null);
  const resto = o.proveedores.filter((p) => p.precision === null);

  const fila = (p: ProveedorRfq): HTMLElement => {
    const marcado = v.proveedores.includes(p.id);
    const casilla = el('input', {
      clase: 'casilla',
      attrs: {
        type: 'checkbox',
        id: `rfq-prov-${p.id}`,
        ...(marcado ? { checked: 'checked' } : {}),
        // Sin correo no se le puede escribir. Se desactiva y se dice por que.
        ...(p.puede_recibir ? {} : { disabled: 'disabled' }),
      },
    });
    casilla.checked = marcado;
    casilla.addEventListener('change', () => {
      // Se muta la lista compartida en vez de repintar: la casilla ya guarda su
      // estado y repintar la lista entera por un clic tira el desplazamiento de
      // quien va marcando de arriba abajo.
      const i = marcados.indexOf(p.id);
      if (casilla.checked && i === -1) marcados.push(p.id);
      if (!casilla.checked && i !== -1) marcados.splice(i, 1);
      onMarcar();
    });

    const correo = p.contactos.find((c) => c.email !== null);

    return el('li', { clase: `proveedor ${p.puede_recibir ? '' : 'proveedor-mudo'}` }, [
      casilla,
      el('label', { clase: 'proveedor-cuerpo', attrs: { for: casilla.id } }, [
        el('span', { clase: 'proveedor-nombre', texto: p.nombre }),
        p.coincidencia === null
          ? null
          : el('span', {
              clase: 'chip chip-coincidencia',
              texto: t(`rfq.coincidencia.${p.coincidencia}`),
            }),
        p.sin_especialidad
          ? el('span', {
              clase: 'chip chip-atencion',
              texto: t('rfq.proveedores.sinEspecialidad'),
            })
          : null,
        // Literal del proveedor: "China-LATAM maritimo". No se traduce.
        ...p.alcances.map((a) => literal(a, 'literal alcance-literal')),
        correo === undefined
          ? el('span', { clase: 'aviso-inline', texto: t('rfq.proveedores.sinCorreo') })
          : el('span', { clase: 'tenue proveedor-correo', texto: correo.email ?? '' }),
      ]),
    ]);
  };

  return el('div', { clase: 'rfq-bloque' }, [
    el('h3', { texto: t('rfq.proveedores.titulo') }),
    cubren.length === 0
      ? null
      : el('div', { clase: 'grupo-proveedores' }, [
          el('h4', { texto: t('rfq.proveedores.cubren') }),
          el('ul', { clase: 'lista-proveedores' }, cubren.map(fila)),
        ]),
    resto.length === 0
      ? null
      : el('div', { clase: 'grupo-proveedores grupo-secundario' }, [
          el('h4', { texto: t('rfq.proveedores.noCubren') }),
          el('p', { clase: 'tenue', texto: t('rfq.proveedores.explicaNoCubren') }),
          el('ul', { clase: 'lista-proveedores' }, resto.map(fila)),
        ]),
  ]);
}

// ---------------------------------------------------------------------------
// La solicitud creada: el correo, el estado y las respuestas
// ---------------------------------------------------------------------------

export interface EstadoFormularioRespuesta {
  readonly proveedorId: string;
  readonly sinOferta: boolean;
  readonly nota: string;
}

export interface OpcionesSolicitud {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly detalle: DetalleSolicitudRfq;
  /** Que se acaba de copiar, como "en:cuerpo". El asunto y el cuerpo se copian
   *  por separado porque van a dos campos distintos del cliente de correo, y
   *  copiarlos juntos deja el asunto pegado dentro del mensaje. */
  readonly copiado: string | null;
  readonly copiaManual: string | null;
  readonly trabajando: boolean;
  readonly respuesta: EstadoFormularioRespuesta;
  readonly onCopiar: (
    clave: string,
    texto: string,
    respaldo: HTMLTextAreaElement | null,
  ) => void;
  readonly onMarcarEnviada: () => void;
  readonly onCerrar: () => void;
  readonly onCambioRespuesta: (v: EstadoFormularioRespuesta) => void;
  readonly onRegistrarRespuesta: () => void;
  readonly onVolver: () => void;
}

export function pintarSolicitud(o: OpcionesSolicitud): HTMLElement {
  const { t, detalle: d } = o;
  const s = d.solicitud;

  return el('section', { clase: 'panel panel-rfq-solicitud' }, [
    el('header', { clase: 'rfq-cabecera' }, [
      el('h2', { texto: `${t('rfq.folio')} ${s.folio}` }),
      el('span', { clase: 'chip chip-estado', texto: t(`rfq.estado.${s.estado}`) }),
      el('span', {
        clase: 'tenue',
        texto:
          `${s.pol.nombre} (${s.pol.pais}) → ${s.pod.nombre} (${s.pod.pais}) · ${s.modo}` +
          (s.equipo === null ? '' : ` · ${s.equipo.codigo}`),
      }),
    ]),

    el('div', { clase: 'rfq-datos' }, [
      dato(t('rfq.carga.mercancia'), literal(s.mercancia)),
      dato(t('rfq.carga.peso'), s.peso_kg),
      dato(t('rfq.carga.volumen'), s.volumen_m3),
      dato(t('rfq.carga.temperatura'), s.temperatura_c),
      dato(
        t('rfq.carga.fechaObjetivo'),
        s.fecha_objetivo === null ? null : formatearFecha(s.fecha_objetivo, o.idioma),
      ),
      dato(t('rfq.carga.requisitos'), literal(s.requisitos)),
    ]),

    pintarDestinatarios(o),
    pintarCorreos(o),

    // El aviso va en la pantalla y no en un comentario: quien vea una solicitud
    // ENVIADA tiene que saber que eso lo afirmo una persona.
    el('p', { clase: 'aviso aviso-integracion', texto: t('rfq.integracion') }),

    el('footer', { clase: 'rfq-acciones' }, [
      el('button', {
        clase: 'boton boton-plano',
        texto: t('rfq.volver'),
        attrs: { type: 'button' },
        onClick: o.onVolver,
      }),
      s.estado === 'CERRADA'
        ? null
        : el('button', {
            clase: 'boton boton-plano',
            texto: t('rfq.cerrar'),
            attrs: { type: 'button' },
            onClick: o.onCerrar,
          }),
      s.estado === 'BORRADOR'
        ? el('button', {
            clase: 'boton boton-primario',
            texto: o.trabajando ? t('rfq.marcando') : t('rfq.marcarEnviada'),
            attrs: o.trabajando
              ? { type: 'button', disabled: 'disabled' }
              : { type: 'button' },
            onClick: o.onMarcarEnviada,
          })
        : null,
    ]),

    pintarRespuestas(o),
  ]);
}

function pintarDestinatarios(o: OpcionesSolicitud): HTMLElement {
  const { t, detalle: d } = o;
  return el('div', { clase: 'rfq-bloque' }, [
    el('h3', { texto: t('rfq.destinatarios') }),
    el(
      'ul',
      { clase: 'lista-destinatarios' },
      d.destinatarios.map((x) =>
        el('li', {}, [
          el('span', { clase: 'proveedor-nombre', texto: x.proveedor_nombre }),
          // El correo congelado: a donde salio, no a donde saldria hoy.
          el('span', { clase: 'tenue', texto: x.email }),
          el('span', { clase: 'chip', texto: x.idioma }),
          el('span', {
            clase: x.enviado_en === null ? 'tenue' : 'chip chip-ok',
            texto:
              x.enviado_en === null
                ? t('rfq.sinEnviar')
                : `${t('rfq.enviadoEn')} ${formatearFecha(x.enviado_en.slice(0, 10), o.idioma)}`,
          }),
        ]),
      ),
    ),
  ]);
}

function pintarCorreos(o: OpcionesSolicitud): HTMLElement {
  const { t, detalle: d } = o;

  return el('div', { clase: 'rfq-bloque rfq-correos' }, [
    el('h3', { texto: t('rfq.correo.titulo') }),
    el('p', { clase: 'tenue', texto: t('rfq.correo.explica') }),
    ...d.correos.map((c) => {
      // Un `textarea` y no un `pre`: se puede seleccionar entero con un click y
      // es lo que permite el respaldo cuando el navegador no da portapapeles.
      const area = el('textarea', {
        clase: 'correo-cuerpo',
        attrs: { id: `rfq-correo-${c.idioma}`, rows: '18', readonly: 'readonly' },
      });
      area.value = c.cuerpo;

      return el('article', { clase: 'correo' }, [
        el('div', { clase: 'correo-cabecera' }, [
          el('span', { clase: 'chip', texto: `${t('rfq.correo.idioma')}: ${c.idioma}` }),
          el('span', {
            clase: 'tenue',
            texto: `${t('rfq.correo.para')}: ${c.para.map((p) => p.email).join(', ')}`,
          }),
        ]),
        dato(t('rfq.correo.asunto'), c.asunto, 'dato correo-asunto'),
        area,
        el('div', { clase: 'correo-acciones' }, [
          el('button', {
            clase: 'boton boton-plano',
            texto:
              o.copiado === `${c.idioma}:asunto`
                ? t('rfq.correo.copiado')
                : t('rfq.correo.copiarAsunto'),
            attrs: { type: 'button' },
            onClick: () => {
              o.onCopiar(`${c.idioma}:asunto`, c.asunto, null);
            },
          }),
          el('button', {
            clase: 'boton boton-secundario',
            texto:
              o.copiado === `${c.idioma}:cuerpo`
                ? t('rfq.correo.copiado')
                : t('rfq.correo.copiarCuerpo'),
            attrs: { type: 'button' },
            onClick: () => {
              o.onCopiar(`${c.idioma}:cuerpo`, c.cuerpo, area);
            },
          }),
          o.copiaManual !== null && o.copiaManual.startsWith(c.idioma)
            ? el('span', { clase: 'aviso-inline', texto: t('rfq.correo.copiaManual') })
            : null,
        ]),
      ]);
    }),
  ]);
}

function pintarRespuestas(o: OpcionesSolicitud): HTMLElement {
  const { t, detalle: d } = o;
  const s = d.solicitud;

  const proveedor = el('select', {
    clase: 'control',
    attrs: { id: 'rfq-resp-proveedor' },
  });
  proveedor.appendChild(
    el('option', { texto: t('rfq.respuestas.eligeProveedor'), attrs: { value: '' } }),
  );
  for (const x of d.destinatarios) {
    proveedor.appendChild(
      el('option', { texto: x.proveedor_nombre, attrs: { value: x.proveedor_id } }),
    );
  }
  proveedor.value = o.respuesta.proveedorId;
  proveedor.addEventListener('change', () => {
    o.onCambioRespuesta({ ...o.respuesta, proveedorId: proveedor.value });
  });

  const sinOferta = el('input', {
    clase: 'casilla',
    attrs: { type: 'checkbox', id: 'rfq-resp-sin-oferta' },
  });
  sinOferta.checked = o.respuesta.sinOferta;
  sinOferta.addEventListener('change', () => {
    o.onCambioRespuesta({ ...o.respuesta, sinOferta: sinOferta.checked });
  });

  const nota = el('input', {
    clase: 'control',
    attrs: { id: 'rfq-resp-nota', type: 'text', value: o.respuesta.nota },
  });
  nota.addEventListener('input', () => {
    o.onCambioRespuesta({ ...o.respuesta, nota: nota.value });
  });

  return el('div', { clase: 'rfq-bloque' }, [
    el('h3', { texto: t('rfq.respuestas.titulo') }),

    d.respuestas.length === 0
      ? el('p', { clase: 'tenue', texto: t('rfq.respuestas.ninguna') })
      : el(
          'ul',
          { clase: 'lista-respuestas' },
          d.respuestas.map((r) =>
            el('li', {}, [
              el('span', { clase: 'proveedor-nombre', texto: r.proveedor_nombre }),
              el('span', {
                clase: 'tenue',
                texto: `${t('rfq.respuestas.respondidaEn')} ${formatearFecha(r.respondida_en.slice(0, 10), o.idioma)}`,
              }),
              // [H59][Invariante 7] "No cubro esa ruta" es una respuesta, y no
              // es lo mismo que no haber contestado.
              r.sin_oferta
                ? el('span', {
                    clase: 'chip chip-atencion',
                    texto: t('rfq.respuestas.sinOferta'),
                  })
                : null,
              r.rate_sheet_id === null
                ? el('span', { clase: 'tenue', texto: t('rfq.respuestas.sinRateSheet') })
                : el('span', { clase: 'chip chip-ok' }, [
                    el('span', { texto: t('rfq.respuestas.rateSheet') }),
                    literal(r.rate_sheet_asunto),
                  ]),
              literal(r.nota),
            ]),
          ),
        ),

    // Registrar una respuesta contra un borrador lo rechaza la base: si el
    // proveedor contesto es que la peticion salio. Se dice antes de intentarlo.
    s.estado === 'BORRADOR'
      ? el('p', { clase: 'tenue', texto: t('rfq.respuestas.soloEnviadas') })
      : el('div', { clase: 'formulario-respuesta' }, [
          campo(t('rfq.respuestas.proveedor'), proveedor),
          el('div', { clase: 'campo campo-casilla' }, [
            sinOferta,
            el('label', {
              clase: 'etiqueta',
              texto: t('rfq.respuestas.sinOferta'),
              attrs: { for: 'rfq-resp-sin-oferta' },
            }),
          ]),
          campo(t('rfq.respuestas.nota'), nota, undefined, 'campo campo-ancho'),
          el('div', { clase: 'campo campo-accion' }, [
            el('button', {
              clase: 'boton boton-secundario',
              texto: o.trabajando
                ? t('rfq.respuestas.registrando')
                : t('rfq.respuestas.registrar'),
              attrs: o.trabajando
                ? { type: 'button', disabled: 'disabled' }
                : { type: 'button' },
              onClick: o.onRegistrarRespuesta,
            }),
          ]),
        ]),
  ]);
}

// ---------------------------------------------------------------------------
// La lista de solicitudes
// ---------------------------------------------------------------------------

export interface OpcionesLista {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly solicitudes: readonly ResumenSolicitudRfq[];
  readonly todas: boolean;
  readonly cargando: boolean;
  readonly onVerTodas: (todas: boolean) => void;
  readonly onAbrir: (id: string) => void;
  /**
   * Salir de la lista.
   *
   * Opcional desde que hay menu lateral: esta lista es la raiz de su zona, asi
   * que quien quiera irse usa el menu. Un "Volver" que salta a otra zona
   * distinta es justo el control sin destino claro que despista a quien acaba
   * de llegar. Se conserva para cuando la lista se abra desde otro sitio.
   */
  readonly onVolver?: () => void;
}

export function pintarListaSolicitudes(o: OpcionesLista): HTMLElement {
  const { t } = o;

  const cabecera = el('tr', {}, [
    el('th', { texto: t('rfq.lista.folio') }),
    el('th', { texto: t('rfq.lista.estado') }),
    el('th', { texto: t('rfq.lista.ruta') }),
    el('th', { clase: 'celda-numero', texto: t('rfq.lista.destinatarios') }),
    // La columna que justifica la lista: de cuantos falta respuesta.
    el('th', { clase: 'celda-numero', texto: t('rfq.lista.sinResponder') }),
    el('th', { texto: t('rfq.lista.enviada') }),
    el('th', {}),
  ]);

  const filas = o.solicitudes.map((s) =>
    el('tr', { clase: s.sin_responder > 0 ? 'fila-espera' : undefined }, [
      el('td', {}, [el('code', { texto: s.folio })]),
      el('td', {}, [
        el('span', { clase: 'chip chip-estado', texto: t(`rfq.estado.${s.estado}`) }),
      ]),
      el('td', {
        texto:
          `${s.pol_nombre} (${s.pol_pais}) → ${s.pod_nombre} (${s.pod_pais})` +
          (s.equipo_codigo === null ? '' : ` · ${s.equipo_codigo}`),
      }),
      el('td', { clase: 'celda-numero', texto: String(s.destinatarios) }),
      el('td', { clase: 'celda-numero', texto: String(s.sin_responder) }),
      el('td', {
        texto:
          s.enviada_en === null
            ? t('rfq.sinEnviar')
            : formatearFecha(s.enviada_en.slice(0, 10), o.idioma),
      }),
      el('td', {}, [
        el('button', {
          clase: 'boton boton-plano boton-abrir',
          texto: t('rfq.lista.abrir'),
          attrs: { type: 'button' },
          onClick: () => {
            o.onAbrir(s.id);
          },
        }),
      ]),
    ]),
  );

  return el('section', { clase: 'panel panel-rfq-lista' }, [
    el('header', { clase: 'rfq-cabecera' }, [
      el('h2', { texto: t('rfq.lista.titulo') }),
      el('button', {
        clase: `boton boton-plano ${o.todas ? '' : 'boton-activo'}`,
        texto: t('rfq.lista.verAbiertas'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onVerTodas(false);
        },
      }),
      el('button', {
        clase: `boton boton-plano ${o.todas ? 'boton-activo' : ''}`,
        texto: t('rfq.lista.verTodas'),
        attrs: { type: 'button' },
        onClick: () => {
          o.onVerTodas(true);
        },
      }),
      o.onVolver === undefined
        ? null
        : el('button', {
            clase: 'boton boton-plano',
            texto: t('rfq.volver'),
            attrs: { type: 'button' },
            onClick: o.onVolver,
          }),
    ]),

    o.cargando
      ? el('p', { clase: 'tenue', texto: t('app.cargando') })
      : o.solicitudes.length === 0
        ? el('p', {
            clase: 'tenue',
            texto: o.todas ? t('rfq.lista.vaciaTodas') : t('rfq.lista.vacia'),
          })
        : el('table', { clase: 'tabla-resultados tabla-solicitudes' }, [
            el('thead', {}, [cabecera]),
            el('tbody', {}, filas),
          ]),
  ]);
}
